import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const REGIONS = ["US", "CA", "GCC", "IN"];
const REGION_CCY = { US: "USD", CA: "CAD", GCC: "AED", IN: "INR" };
const STAFFING = ["permanent", "contract", "c2h", "temp", "executive_search", "retained_search", "rpo", "msp"];
const WORK = ["Onsite", "Hybrid", "Remote"];
const PRIORITY = ["High", "Medium", "Low"];
const STATUS = ["draft", "open", "on_hold", "filled", "closed"];

const EMPTY = {
  title: "", client_id: "", requisition_id: "", region: "US", currency: "USD", location: "",
  work_model: "Onsite", employment_type: "Full-time", staffing_type: "permanent", openings: 1,
  priority: "Medium", status: "open", comp_min: "", comp_max: "", required_experience: "",
  required_skills: "", preferred_skills: "", description: "", internal_notes: "",
  education: "", certifications: "", interview_process: "", submission_instructions: "",
  target_start_date: "", target_fill_date: "", hold_reason: "", close_reason: "",
  screening_questions: [],
  recruitment_fee: "", fee_model: "percentage", payment_terms: "", guarantee_period: "",
  estimated_revenue: "", bill_rate: "", pay_rate: "",
};

export default function JobForm({ open, onClose, onSaved, job }) {
  const { can } = useAuth();
  const isEdit = !!job;
  const commercial = can("job:view_commercial");
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/clients", { params: { page_size: 100 } }).then(({ data }) => setClients(data.items || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (job) setForm({ ...EMPTY, ...job, screening_questions: job.screening_questions || [],
      required_skills: (job.required_skills || []).join(", "),
      preferred_skills: (job.preferred_skills || []).join(", "),
      comp_min: job.comp_min ?? "", comp_max: job.comp_max ?? "",
      recruitment_fee: job.recruitment_fee ?? "", estimated_revenue: job.estimated_revenue ?? "",
      bill_rate: job.bill_rate ?? "", pay_rate: job.pay_rate ?? "" });
    else setForm(EMPTY);
  }, [job, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim() || !form.client_id) { toast.error("Title and client are required"); return; }
    setSaving(true);
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = { ...form,
      openings: Number(form.openings) || 1,
      comp_min: num(form.comp_min), comp_max: num(form.comp_max),
      required_skills: form.required_skills ? form.required_skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
      preferred_skills: form.preferred_skills ? form.preferred_skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
      screening_questions: (form.screening_questions || []).filter((q) => (q.question || "").trim()),
      recruitment_fee: num(form.recruitment_fee), estimated_revenue: num(form.estimated_revenue),
      bill_rate: num(form.bill_rate), pay_rate: num(form.pay_rate) };
    try {
      const res = isEdit ? await api.put(`/jobs/${job.id}`, payload) : await api.post("/jobs", payload);
      toast.success(isEdit ? "Job updated" : "Job created");
      onSaved?.(res.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto scrollbar-thin" data-testid="job-form-sheet">
        <SheetHeader><SheetTitle className="font-display">{isEdit ? "Edit Job" : "New Job / Requisition"}</SheetTitle>
          <SheetDescription>Requisition details. Commercial terms are visible only to authorized users.</SheetDescription></SheetHeader>
        <div className="space-y-4 mt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Job Title *"><Input data-testid="jf-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></F>
            <F label="Client *">
              <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger data-testid="jf-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Requisition ID"><Input value={form.requisition_id} onChange={(e) => set("requisition_id", e.target.value)} /></F>
            <F label="Staffing Type">
              <Select value={form.staffing_type} onValueChange={(v) => set("staffing_type", v)}>
                <SelectTrigger data-testid="jf-staffing"><SelectValue /></SelectTrigger>
                <SelectContent>{STAFFING.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Region">
              <Select value={form.region} onValueChange={(v) => { set("region", v); set("currency", REGION_CCY[v]); }}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} /></F>
            <F label="Work Model">
              <Select value={form.work_model} onValueChange={(v) => set("work_model", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{WORK.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Employment Type"><Input value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)} /></F>
            <F label="Openings"><Input type="number" value={form.openings} onChange={(e) => set("openings", e.target.value)} /></F>
            <F label="Priority">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="jf-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Comp Min"><Input type="number" value={form.comp_min} onChange={(e) => set("comp_min", e.target.value)} /></F>
            <F label="Comp Max"><Input type="number" value={form.comp_max} onChange={(e) => set("comp_max", e.target.value)} /></F>
            <F label="Required Experience"><Input value={form.required_experience} onChange={(e) => set("required_experience", e.target.value)} /></F>
            <F label="Education"><Input data-testid="jf-education" value={form.education} onChange={(e) => set("education", e.target.value)} /></F>
            <F label="Certifications"><Input data-testid="jf-certifications" value={form.certifications} onChange={(e) => set("certifications", e.target.value)} /></F>
            <F label="Target Start Date"><Input type="date" value={(form.target_start_date || "").slice(0, 10)} onChange={(e) => set("target_start_date", e.target.value)} /></F>
            <F label="Deadline (target fill date)"><Input type="date" data-testid="jf-deadline" value={(form.target_fill_date || "").slice(0, 10)} onChange={(e) => set("target_fill_date", e.target.value)} /></F>
          </div>
          {form.status === "on_hold" && (
            <F label="Hold Reason *"><Textarea rows={2} data-testid="jf-hold-reason" value={form.hold_reason || ""} onChange={(e) => set("hold_reason", e.target.value)} /></F>
          )}
          {form.status === "closed" && (
            <F label="Close Reason *"><Textarea rows={2} data-testid="jf-close-reason" value={form.close_reason || ""} onChange={(e) => set("close_reason", e.target.value)} /></F>
          )}
          <F label="Required Skills (comma separated)"><Input data-testid="jf-skills" value={form.required_skills} onChange={(e) => set("required_skills", e.target.value)} /></F>
          <F label="Preferred Skills (comma separated)"><Input value={form.preferred_skills} onChange={(e) => set("preferred_skills", e.target.value)} /></F>
          <F label="Job Description"><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
          <F label="Interview Process"><Textarea rows={2} data-testid="jf-interview-process" value={form.interview_process || ""} onChange={(e) => set("interview_process", e.target.value)} /></F>
          <F label="Recruiter Instructions"><Textarea rows={2} data-testid="jf-recruiter-instructions" value={form.submission_instructions || ""} onChange={(e) => set("submission_instructions", e.target.value)} /></F>
          <F label="Internal Recruiter Notes"><Textarea rows={2} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} /></F>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Screening Questions</Label>
              <Button type="button" size="sm" variant="outline" data-testid="jf-add-question"
                onClick={() => set("screening_questions", [...(form.screening_questions || []), { question: "", required: true }])}>Add question</Button>
            </div>
            {(form.screening_questions || []).map((q, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input data-testid={`jf-question-${i}`} value={q.question || ""} placeholder={`Question ${i + 1}`}
                  onChange={(e) => { const next = [...form.screening_questions]; next[i] = { ...next[i], question: e.target.value }; set("screening_questions", next); }} />
                <Button type="button" size="sm" variant="ghost" data-testid={`jf-remove-question-${i}`}
                  onClick={() => set("screening_questions", form.screening_questions.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            ))}
          </div>

          {commercial && (
            <div className="rounded-lg border border-[#255058]/30 bg-[#255058]/5 p-4 space-y-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#255058]">Commercial (restricted)</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Recruitment Fee (%)"><Input type="number" data-testid="jf-fee" value={form.recruitment_fee} onChange={(e) => set("recruitment_fee", e.target.value)} /></F>
                <F label="Payment Terms"><Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} /></F>
                <F label="Guarantee Period"><Input value={form.guarantee_period} onChange={(e) => set("guarantee_period", e.target.value)} /></F>
                <F label="Estimated Revenue"><Input type="number" value={form.estimated_revenue} onChange={(e) => set("estimated_revenue", e.target.value)} /></F>
                <F label="Bill Rate"><Input type="number" value={form.bill_rate} onChange={(e) => set("bill_rate", e.target.value)} /></F>
                <F label="Pay Rate"><Input type="number" value={form.pay_rate} onChange={(e) => set("pay_rate", e.target.value)} /></F>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-6 pb-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-[#255058] hover:bg-[#1c3d43] text-white" onClick={submit} disabled={saving} data-testid="jf-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create Job")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
